import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { usePageMeta } from '../hooks/usePageMeta';
import ScrollToTop from '../components/ScrollToTop';
import Reveal from '../components/Reveal';
import Hero from '../components/Hero';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import svanseleImg from '../assets/images/svanselegront.webp';
import geoImageImg from '../assets/images/geoatrium.webp';
import norsjoImg from '../assets/images/geoatrium-norsjo.webp';
import hrLandingImg from '../assets/images/hr_landingpage.webp';
import htmxPhpImg from '../assets/images/HTMX-und-PHP.png';
import demografieImg from '../assets/images/demografie-net.png';
import geschichteImg from '../assets/images/geschichte-in-karten.webp';
import schwedenImg from '../assets/images/schweden-praktisch.webp';
import liedzeitImg from '../assets/images/header_liedtext.png';
import nybyggareImg from '../assets/images/nybyggare-card.webp';
import videntoImg from '../assets/images/vidento.webp';

const { FiExternalLink, FiPlay, FiGlobe, FiVideo } = FiIcons;

const References = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const ref = t.references;
  usePageMeta({ title: ref.heroTitle, description: t.hero.description, path: 'references' });

  const websites = [
    {
      title: 'Vidento.EU',
      url: 'https://vidento.eu/',
      description: ref.websites.vidento,
      color: 'from-green-700 to-emerald-800',
      image: videntoImg,
    },
    {
      title: 'Liedzeit',
      url: 'https://liedzeit.de/',
      videoUrl: 'https://videos.alitero.eu/c/liedzeit/videos',
      description: ref.websites.liedzeit,
      color: 'from-rose-500 to-pink-600',
      image: liedzeitImg,
    },
    {
      title: 'demografie.net',
      url: 'https://www.demografie.net/',
      description: ref.websites.demografie,
      color: 'from-teal-500 to-emerald-600',
      image: demografieImg,
    },
    {
      title: 'GeoAtrium',
      url: 'https://geoatrium.com/',
      description: ref.websites.geoimage,
      color: 'from-blue-500 to-blue-700',
      image: geoImageImg,
    },
    {
      title: 'Norsjö GeoAtrium',
      url: 'https://norsjo.geoatrium.com/',
      description: ref.websites.norsjo,
      color: 'from-sky-500 to-cyan-600',
      image: norsjoImg,
    },
    {
      title: 'Schweden praktisch',
      url: 'https://schweden-heute.de/',
      videoUrl: 'https://videos.alitero.eu/c/schweden_praktisch/videos',
      description: ref.websites.schwedenPraktisch,
      color: 'from-yellow-400 to-blue-600',
      image: schwedenImg,
    },
    {
      title: 'Geschichte in Karten',
      url: 'https://geschichte-in-karten.de/',
      videoUrl: 'https://videos.alitero.eu/c/geschichte_in_karten_das_original/videos',
      description: ref.websites.geschichteInKarten,
      color: 'from-amber-600 to-yellow-700',
      image: geschichteImg,
    },
    {
      title: 'Svansele Grönt',
      url: 'https://svanselegront.se./',
      description: ref.websites.svansele,
      color: 'from-green-500 to-emerald-600',
      image: svanseleImg,
    },
    {
      title: 'HR Landingpage',
      url: 'https://hr-1.alitero.de/',
      description: ref.websites.hrLanding,
      color: 'from-purple-500 to-indigo-600',
      image: hrLandingImg,
    },
    {
      title: 'Nybyggare 2.0',
      url: 'https://nybyggare.eu/',
      videoUrl: 'https://videos.alitero.eu/c/nybyggare/videos',
      description: ref.websites.nybyggare,
      color: 'from-green-700 to-emerald-800',
      image: nybyggareImg,
    },
  ];

  const videos = [
    { id: 'f4VwBDUS1Ik', title: ref.videos.video1Title, description: ref.videos.video1Desc },
    { id: 'RHpdh4BxbH4', title: ref.videos.video2Title, description: ref.videos.video2Desc },
    { id: 'lIVRtARXunQ', title: ref.videos.video3Title, description: ref.videos.video3Desc },
    { id: 'UuE0reByaHk', title: ref.videos.video4Title, description: ref.videos.video4Desc, thumbnail: htmxPhpImg },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero
        size="lg"
        gradientClass="hero-gradient-references"
        eyebrow={ref.heroLabel}
        title={ref.heroTitle}
        description={ref.heroDescription}
      />

      {/* Websites Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-800 mb-4">
              <SafeIcon icon={FiGlobe} className="h-7 w-7" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              {ref.websitesTitle}
            </h2>
            <p className="text-lg text-stone-500 max-w-2xl mx-auto">
              {ref.websitesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {websites.map((site, index) => (
              <Reveal
                key={index}
                delay={(index % 2) * 0.1}
                whileHover={{ y: -6 }}
                className="group rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 card-hover border border-stone-200"
              >
                {/* Card Header */}
                <a href={site.url} target="_blank" rel="noopener noreferrer" className="block relative">
                  <div className={`h-48 bg-gradient-to-br ${site.color} relative flex items-center justify-center overflow-hidden`}>
                    {site.image ? (
                      <img
                        src={site.image}
                        alt={site.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-center text-white">
                        <SafeIcon icon={FiGlobe} className="h-12 w-12 mx-auto mb-3 opacity-80" />
                        <p className="text-xl font-bold">{site.title}</p>
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <SafeIcon icon={FiExternalLink} className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </a>
                {/* Card Body */}
                <div className="p-6 bg-white">
                  <h3 className="font-serif text-xl font-bold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">
                    {site.title}
                  </h3>
                  <p className="text-stone-600 mb-3">{site.description}</p>
                  <div className="flex flex-wrap gap-4">
                    <a href={site.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm font-medium text-amber-700 hover:text-amber-800">
                      {ref.visitWebsite}
                      <SafeIcon icon={FiExternalLink} className="ml-1 h-4 w-4" />
                    </a>
                    {site.videoUrl && (
                      <a href={site.videoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-800">
                        {ref.watchVideos}
                        <SafeIcon icon={FiPlay} className="ml-1 h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-red-600 mb-4">
              <SafeIcon icon={FiVideo} className="h-7 w-7" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              {ref.videosTitle}
            </h2>
            <p className="text-lg text-stone-500 max-w-2xl mx-auto">
              {ref.videosSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {videos.map((video, index) => (
              <Reveal
                as="a"
                key={index}
                delay={(index % 2) * 0.1}
                whileHover={{ y: -6 }}
                href={`https://youtu.be/${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 card-hover bg-white border border-stone-200"
              >
                {/* YouTube Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={FiPlay} className="h-7 w-7 text-white ml-1" />
                    </div>
                  </div>
                </div>
                {/* Card Body */}
                <div className="p-6">
                  <h3 className="font-serif text-lg font-bold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-stone-600 text-sm">{video.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ScrollToTop />
    </div>
  );
};

export default References;
